import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usersAPI } from "../utils/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Shield,
  User,
  Mail,
  Eye,
  EyeOff,
  Copy,
  Check,
  X,
  UserCog,
  Users,
  PenTool,
} from "lucide-react";

const ROLE_COLORS = {
  super_admin: "bg-purple-100 text-purple-800 border-purple-300",
  editor: "bg-blue-100 text-blue-800 border-blue-300",
  author: "bg-green-100 text-green-800 border-green-300",
};

const ROLE_ICONS = {
  super_admin: <Shield className="w-4 h-4" />,
  editor: <UserCog className="w-4 h-4" />,
  author: <PenTool className="w-4 h-4" />,
};

export const AdminUsers = () => {
  const { user: currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({ roles: [], permissions: {} });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "author",
  });

  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate("/admin/login");
    } else if (currentUser && currentUser.role !== "super_admin") {
      toast.error("Access denied. Super Admin only.");
      navigate("/admin");
    }
  }, [currentUser, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        usersAPI.getAll(),
        usersAPI.getRoles(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied. Super Admin only.");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (currentUser?.role === "super_admin") {
      fetchData();
    }
  }, [currentUser, fetchData]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await usersAPI.create(formData);
      const newUser = response.data;

      // Check if temp_password was returned
      if (newUser.temp_password) {
        setTempPassword(newUser.temp_password);
      }

      toast.success("User created successfully");
      fetchData();

      // Don't close modal yet - show temp password
      if (!newUser.temp_password) {
        closeModal();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.update(editingUser.id, {
        name: formData.name,
        role: formData.role,
        is_active: formData.is_active,
      });
      toast.success("User updated successfully");
      fetchData();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await usersAPI.delete(userId);
      toast.success("User deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setTempPassword(null);
    setFormData({ email: "", name: "", role: "author" });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setTempPassword(null);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      is_active: user.is_active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setTempPassword(null);
    setShowTempPassword(false);
    setCopiedPassword(false);
    setFormData({ email: "", name: "", role: "author" });
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopiedPassword(true);
    toast.success("Password copied to clipboard");
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red"></div>
      </div>
    );
  }

  if (!currentUser || currentUser.role !== "super_admin") return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-users-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-2 text-brand-green hover:text-brand-red transition-colors mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-playfair font-bold text-brand-green flex items-center gap-3">
                <Users className="w-10 h-10" />
                User Management
              </h1>
              <p className="text-gray-600 mt-2">
                Manage CMS users and their roles
              </p>
            </div>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 font-bold hover:bg-brand-red/90 transition-colors uppercase tracking-wide rounded-lg"
              data-testid="create-user-button"
            >
              <Plus className="w-5 h-5" />
              Add User
            </button>
          </div>
        </div>

        {/* Role Legend */}
        <div className="bg-white border-2 border-gray-900 p-4 mb-8 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-3">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${ROLE_COLORS.super_admin}`}
              >
                {ROLE_ICONS.super_admin} Super Admin
              </span>
              <span className="text-gray-600">Full access to all features</span>
            </div>
            <div className="flex items-start gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${ROLE_COLORS.editor}`}
              >
                {ROLE_ICONS.editor} Editor
              </span>
              <span className="text-gray-600">
                Publish articles, manage pSEO
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${ROLE_COLORS.author}`}
              >
                {ROLE_ICONS.author} Author
              </span>
              <span className="text-gray-600">
                Create articles (drafts only)
              </span>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white border-2 border-gray-900 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-gray-50 border-b-2 border-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                  Role
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                  Joined
                </th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="hover:bg-gray-50"
                  data-testid={`user-row-${user.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#3A86FF] to-[#8A4DFF] rounded-full flex items-center justify-center text-white font-bold">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase border ${ROLE_COLORS[user.role] || ROLE_COLORS.author}`}
                    >
                      {ROLE_ICONS[user.role]} {user.role?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${user.is_active !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                    >
                      {user.is_active !== false ? "Active" : "Inactive"}
                    </span>
                    {user.must_change_password && (
                      <span className="ml-2 text-xs text-amber-600">
                        (Must change password)
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-2 text-gray-600 hover:text-brand-green transition-colors"
                        title="Edit user"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {user.id !== currentUser.id && (
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 text-gray-600 hover:text-brand-red transition-colors"
                          title="Delete user"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No users found. Click "Add User" to create one.
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="user-modal"
          >
            {tempPassword ? (
              // Show temporary password after creation
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-playfair font-bold text-gray-900 mb-2">
                  User Created!
                </h3>
                <p className="text-gray-600 mb-6">
                  Share this temporary password with{" "}
                  <strong>{formData.name}</strong>. They will be asked to change
                  it on first login.
                </p>

                <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-4 mb-6">
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
                    Temporary Password
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-lg font-mono bg-white px-3 py-2 rounded border">
                      {showTempPassword ? tempPassword : "••••••••••••"}
                    </code>
                    <button
                      onClick={() => setShowTempPassword(!showTempPassword)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                      title={showTempPassword ? "Hide" : "Show"}
                    >
                      {showTempPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={copyPassword}
                      className="p-2 text-gray-500 hover:text-brand-green"
                      title="Copy"
                    >
                      {copiedPassword ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  onClick={closeModal}
                  className="w-full py-3 bg-brand-green text-white font-bold rounded-lg hover:bg-brand-green/90 transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              // Create/Edit form
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-playfair font-bold text-gray-900">
                    {editingUser ? "Edit User" : "Create New User"}
                  </h3>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form
                  onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
                  className="space-y-4"
                >
                  {!editingUser && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                        placeholder="user@example.com"
                        data-testid="user-email-input"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="Full name"
                      data-testid="user-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role *
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) =>
                        setFormData({ ...formData, role: e.target.value })
                      }
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      data-testid="user-role-select"
                    >
                      <option value="author">
                        Author - Create articles (drafts only)
                      </option>
                      <option value="editor">
                        Editor - Publish articles, manage pSEO
                      </option>
                      <option value="super_admin">
                        Super Admin - Full access
                      </option>
                    </select>
                  </div>

                  {editingUser && (
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_active !== false}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              is_active: e.target.checked,
                            })
                          }
                          className="w-4 h-4 text-brand-green rounded"
                        />
                        <span className="text-sm text-gray-700">
                          Account Active
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 py-3 bg-brand-green text-white font-bold rounded-lg hover:bg-brand-green/90 transition-colors"
                      data-testid="save-user-button"
                    >
                      {editingUser ? "Update User" : "Create User"}
                    </button>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 py-3 border-2 border-gray-300 font-bold rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
